import { Component, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { fadeInOnEnterAnimation } from 'angular-animations';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { Upgrade } from 'src/app/_models/upgrade';
import { Subscription, forkJoin } from 'rxjs';
import { UpgradeService } from '../../_services/upgrade.service';
import { NotifyService } from 'src/app/_services/notify.service';
import { Asset } from 'src/app/_models/asset';
import { finalize } from 'rxjs/operators';
import { Supplier } from 'src/app/_models/supplier';
import { SupplierService } from 'src/app/setup/_services/supplier.service';
import { ImportAssetService } from 'src/app/integration/_services/asset-import.service';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';

@Component({
   selector: 'app-upgrade-edit',
   templateUrl: './upgrade-edit.component.html',
   styleUrls: ['./upgrade-edit.component.scss'],
   animations: [fadeInOnEnterAnimation()],
})
export class UpgradeEditComponent implements OnInit, OnDestroy {
   routerAccess = false;
   opened = false;
   isNewMode = true;
   isOriginateFromWip = false;
   id: number;
   barcode: string;
   ModalTitle = 'Create New Upgrade';
   upgradeForm: UntypedFormGroup;
   upgrade: Upgrade;
   loading = false;
   suppliers: Supplier[];
   isAssetImport = false;
   assetImportId: number;
   public dataSuppliers: Array<Supplier>;
   @Output()
   dialogClose: EventEmitter<Upgrade> = new EventEmitter<Upgrade>();
   @Output()
   dialogCloseIsSavedFromUpgrade: EventEmitter<boolean> = new EventEmitter<boolean>();
   private upgradeEditSubscription: Subscription;
   private importAssetSubscription: Subscription;
   constructor(
      private upgradeService: UpgradeService,
      private supplierService: SupplierService,
      private fb: UntypedFormBuilder,
      private notify: NotifyService,
      private importAssetService: ImportAssetService,
      private route: ActivatedRoute,
      private _location: Location
   ) {}

   ngOnInit() {
      // We can access this through routing or an event. If routing has no parameters we know its an event
      this.route.paramMap.subscribe((params) => {
         if (params.get('id')) {
            this.routerAccess = true;
            this.setupComponent(+params.get('id'));
         }
      });

      this.upgradeEditSubscription = this.upgradeService.upgradeSelected.subscribe((d: Upgrade) => {
         if (d) {
            this.setupComponent(d.id);
         } else {
            this.setupComponent(0);
         }
      });

      this.importAssetSubscription = this.upgradeService.ImportSelected.subscribe((d: Upgrade) => {
         this.createForm();
         if (d) {
            this.isAssetImport = true;
            this.isNewMode = true;

            this.supplierService.getSuppliersFull().subscribe(
               (_suppliers) => {
                  this.suppliers = _suppliers;
                  this.dataSuppliers = this.suppliers.slice();
                  this.opened = true;
                  this.assetImportId = d.assetImportId;
                  this.upgradeForm.setValue({
                     barcode: '',
                     barcodeValidate: '',
                     upgradeDate: new Date(d.upgradeDate),
                     upgradeAmount: d.upgradeAmount,
                     description: '',
                     supplierId: d.supplierId,
                     invoiceNumber: d.invoiceNumber,
                     tenderNumber: '',
                     referenceNumber: '',
                     voteNumber: d.voteNumber,
                     journalRef: '',
                  });
               },
               (error) => {
                  const message = error;
                  this.notify.error(message);
               }
            );
         }
      });
   }

   setupComponent(id: number) {
      this.isOriginateFromWip = false;
      if (id > 0) {
         this.createForm();
         this.ModalTitle = 'Correct Upgrade';
         this.isNewMode = false;
         this.id = id;
         this.upgradeForm.controls['barcode'].disable();
         forkJoin([this.upgradeService.getUpgrade(this.id), this.supplierService.getSuppliersFull()]).subscribe(([_upgrade, _suppliers]) => {
            this.upgrade = _upgrade;
            this.isOriginateFromWip = this.upgrade.isOriginateFromWip;
            this.suppliers = _suppliers;
            this.dataSuppliers = this.suppliers.slice();
            this.barcode = this.upgrade.barcode;
            this.upgradeForm.setValue({
               barcode: this.upgrade.barcode,
               barcodeValidate: this.upgrade.barcode,
               upgradeDate: new Date(this.upgrade.upgradeDate),
               upgradeAmount: this.upgrade.upgradeAmount,
               description: this.upgrade.description,
               supplierId: this.upgrade.supplierId,
               invoiceNumber: this.upgrade.invoiceNumber,
               tenderNumber: this.upgrade.tenderNumber,
               referenceNumber: this.upgrade.referenceNumber,
               voteNumber: this.upgrade.voteNumber,
               journalRef: this.upgrade.journalRef,
            });

            this.opened = true;
         });
      } else {
         this.opened = true;
         this.ModalTitle = 'Create Upgrade';
         this.isNewMode = true;
         this.createForm();
         this.supplierService.getSuppliersFull().subscribe((_suppliers) => {
            this.suppliers = _suppliers;
            this.dataSuppliers = this.suppliers.slice();
         });
         this.upgradeForm.setValue({
            barcode: '',
            barcodeValidate: '',
            upgradeDate: '',
            upgradeAmount: '',
            description: '',
            supplierId: '',
            invoiceNumber: '',
            tenderNumber: '',
            referenceNumber: '',
            voteNumber: '',
            journalRef: '',
         });
         this.upgradeForm.controls['barcode'].disable();
      }
      this.createForm();
   }

   ngOnDestroy() {
      this.upgradeEditSubscription.unsubscribe();
      this.importAssetSubscription.unsubscribe();
   }

   createForm() {
      this.upgradeForm = this.fb.group({
         barcode: ['', Validators.compose([Validators.required, Validators.maxLength(50)])],
         barcodeValidate: ['', Validators.compose([Validators.required, Validators.maxLength(50)])],
         upgradeDate: ['', Validators.compose([Validators.required])],
         upgradeAmount: [''],
         description: ['', Validators.compose([Validators.required, Validators.maxLength(500)])],
         supplierId: [null, Validators.maxLength(200)],
         invoiceNumber: ['', Validators.maxLength(50)],
         voteNumber: ['', Validators.maxLength(50)],
         tenderNumber: ['', Validators.maxLength(50)],
         referenceNumber: ['', Validators.maxLength(100)],
         journalRef: [''],
      });
   }
   assetSelected(asset: Asset) {
      if (asset.status !== 'INUSE') {
         const message = 'Only an asset with a status of INUSE can be upgraded';
         this.notify.error(message);
      } else {
         this.barcode = asset.assetBarcode;
         this.upgradeForm.patchValue({ barcode: asset.assetBarcode, barcodeValidate: asset.assetBarcode });
      }
   }
   close(successOrFail: boolean) {
      this.opened = false;
      this.dialogClose.emit(null);
      // should only emit true if it was a new creation and not an import or update
      this.dialogCloseIsSavedFromUpgrade.emit(successOrFail);
      this.upgradeForm.reset();
   }
   handleFilterSupplier(value) {
      this.dataSuppliers = this.suppliers.filter((s) => s.name.toLowerCase().indexOf(value.toLowerCase()) !== -1 || s.id.toLowerCase().indexOf(value.toLowerCase()) !== -1);
   }
   save() {
      this.loading = true;
      this.upgrade = Object.assign({}, this.upgradeForm.value);
      this.upgrade.upgradeDate = this.upgradeForm.value.upgradeDate.toLocaleDateString('en-ZA');
      this.upgrade.barcode = this.barcode;
      if (this.isNewMode) {
         this.upgradeService
            .createUpgrade(this.upgrade)
            .pipe(
               finalize(() => {
                  this.loading = false;
               })
            )
            .subscribe(
               () => {
                  if (this.isAssetImport) {
                     this.importAssetService.processUpgrade(this.assetImportId, this.upgrade).subscribe(
                        () => {
                           const message = 'Successfully Upgraded Asset';
                           this.notify.success(message);
                           this.close(true);
                        },
                        (error) => {
                           const message = error;
                           this.notify.error(message);
                        }
                     );
                  } else {
                     const message = 'Successfully Created';
                     this.notify.success(message);
                     this.close(true);
                  }
               },
               (error) => {
                  const message = error;
                  this.notify.error(message);
               }
            );
      } else {
         this.upgrade.id = this.id;
         this.upgradeService
            .updateUpgrade(this.id, this.upgrade)
            .pipe(
               finalize(() => {
                  this.loading = false;
               })
            )
            .subscribe(
               () => {
                  const message = 'Successfully Updated';
                  this.notify.success(message);
                  this.close(false);
               },
               (error) => {
                  const message = error;
                  this.notify.error(message);
               }
            );
      }
   }
   back() {
      this._location.back();
   }
}
